import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api";
import { Scanner } from "@yudiel/react-qr-scanner";

import { useI18n } from "../i18n/I18nContext";

function ParcInput({ label, value, onChange, onScan }) {
  return (
    <div className="fab-section">
      <div className="fab-section-label fab-section-label-green">🔧 {label}</div>
      <div className="fab-parc-row">
        <input value={value} onChange={(e) => onChange(e.target.value)} className="fab-parc-input" placeholder="Saisir ou scanner…" />
        <button type="button" onClick={onScan} className="fab-scan-btn" title="Scanner QR">📷</button>
      </div>
    </div>
  );
}

function EmpilementInput({ label, value, searchValue, onSearchChange, onSelect, onClear, onScan, showList, setShowList, filteredList, boxRef }) {
  return (
    <div className="fab-section">
      <div className="fab-section-label fab-section-label-green">📦 {label}</div>
      <div ref={boxRef} style={{ position: "relative" }}>
        <div className="fab-parc-row">
          <input
            value={searchValue}
            onChange={(e) => { onSearchChange(e.target.value); setShowList(true); }}
            onFocus={() => setShowList(true)}
            placeholder="Sélectionner ou filtrer un PN"
            className="fab-parc-input"
          />
          <button type="button" onClick={onScan} className="fab-scan-btn" title="Scanner QR">📷</button>
        </div>
        {value && (
          <div className="fab-selected-badge">
            <span>✔ Sélectionné :</span>
            <strong>{value}</strong>
            <button type="button" className="fab-clear-btn" onClick={onClear}>✕</button>
          </div>
        )}
        {showList && filteredList.length > 0 && (
          <div className="fab-dropdown" onMouseDown={(e) => e.preventDefault()}>
            {filteredList.map((p) => (
              <div key={p} className="fab-dropdown-item"
                onClick={() => { onSelect(p); setShowList(false); }}>
                {p}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Fabrication() {
  const { t, ui } = useI18n();

  const navigate = useNavigate();

  const [operator, setOperator] = useState("");
  const [now, setNow]           = useState(new Date());

  const [parcPresseGauche, setParcPresseGauche]         = useState("");
  const [parcPresseDroite, setParcPresseDroite]         = useState("");
  const [parcEmpilementGauche, setParcEmpilementGauche] = useState("");
  const [searchGauche, setSearchGauche]                 = useState("");
  const [showListGauche, setShowListGauche]             = useState(false);
  const boxRefGauche                                    = useRef(null);
  const [parcEmpilementDroite, setParcEmpilementDroite] = useState("");
  const [searchDroite, setSearchDroite]                 = useState("");
  const [showListDroite, setShowListDroite]             = useState(false);
  const boxRefDroite                                    = useRef(null);
  const [empilementList, setEmpilementList]             = useState([]);

  const [presseUtilisee, setPresseUtilisee]                   = useState("");
  const [dureeCycle, setDureeCycle]                           = useState("");
  const [calesUtilisees, setCalesUtilisees]                   = useState("");
  const [traitementApresCuisson, setTraitementApresCuisson]   = useState("");
  const [commentaire, setCommentaire]                         = useState("");
  const [commentairesGenerales, setCommentairesGenerales]     = useState("");

  const [sending, setSending] = useState(false);
  const [error, setError]     = useState("");
  const [success, setSuccess] = useState("");
  const [showScanner, setShowScanner] = useState(false);
  const [scanTarget, setScanTarget]   = useState(null);

  useEffect(() => {
    const raw = localStorage.getItem("user");
    if (raw) { try { setOperator(JSON.parse(raw).username || ""); } catch (e) { console.error(e); } }
  }, []);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    async function loadEmpilement() {
      try {
        const res  = await apiFetch("/api/empilement/list/fabrication");
        if (!res.ok) return;
        const data = await res.json().catch(() => ({}));
        const raw  = Array.isArray(data) ? data : Array.isArray(data.items) ? data.items : [];
        const all  = raw
          .map((it) => typeof it === "string" ? it : it.StackId || it.stackId || it.PN || it.pn || "")
          .map((x) => String(x).trim()).filter(Boolean);
        setEmpilementList(Array.from(new Set(all)));
      } catch (e) { console.error(e); }
    }
    loadEmpilement();
  }, []);

  useEffect(() => {
    function handleClickOutside(e) {
      if (boxRefGauche.current && !boxRefGauche.current.contains(e.target)) setShowListGauche(false);
      if (boxRefDroite.current && !boxRefDroite.current.contains(e.target)) setShowListDroite(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  function normalize(text) {
    return text.toString().toLowerCase().normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, " ").trim();
  }

  const filteredGauche = useMemo(() => {
    const s = normalize(searchGauche);
    return empilementList.filter((p) => !s || normalize(p).includes(s));
  }, [searchGauche, empilementList]);

  const filteredDroite = useMemo(() => {
    const s = normalize(searchDroite);
    return empilementList.filter((p) => !s || normalize(p).includes(s));
  }, [searchDroite, empilementList]);

  function openScan(target) { setScanTarget(target); setShowScanner(true); }

  function handleScan(results) {
    if (!results?.length) return;
    const pn = (results[0].rawValue || "").trim();
    if (!pn) return;
    if (scanTarget === "presseGauche")     setParcPresseGauche(pn);
    if (scanTarget === "presseDroite")     setParcPresseDroite(pn);
    if (scanTarget === "empilementGauche") { setParcEmpilementGauche(pn); setSearchGauche(pn); setShowListGauche(false); }
    if (scanTarget === "empilementDroite") { setParcEmpilementDroite(pn); setSearchDroite(pn); setShowListDroite(false); }
    setShowScanner(false); setScanTarget(null);
  }

  async function handleSubmit(e) {
    e.preventDefault(); setError(""); setSuccess("");
    if (!parcPresseGauche.trim())     return setError("Merci de renseigner le Parc Number Presse Gauche.");
    if (!parcPresseDroite.trim())     return setError("Merci de renseigner le Parc Number Presse Droite.");
    if (!parcEmpilementGauche.trim()) return setError("Merci de renseigner le Parc Number Empilement Gauche.");
    if (!parcEmpilementDroite.trim()) return setError("Merci de renseigner le Parc Number Empilement Droite.");

    const payload = {
      operator,
      parcPresseGauche:       parcPresseGauche.trim(),
      parcPresseDroite:       parcPresseDroite.trim(),
      parcEmpilementGauche:   parcEmpilementGauche.trim(),
      parcEmpilementDroite:   parcEmpilementDroite.trim(),
      presseUtilisee:         presseUtilisee.trim(),
      dureeCycle:             dureeCycle.trim(),
      calesUtilisees:         calesUtilisees.trim(),
      commentaire:            commentaire.trim(),
      traitementApresCuisson: traitementApresCuisson.trim(),
      commentairesGenerales:  commentairesGenerales.trim(),
      timestamp: new Date().toISOString(),
      source: "FabricationPressageCuissonRefroidissement",
    };

    try {
      setSending(true);
      const res = await apiFetch("/api/fabrication", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.message || "Erreur serveur");
      }
      setParcPresseGauche(""); setParcPresseDroite("");
      setParcEmpilementGauche(""); setSearchGauche("");
      setParcEmpilementDroite(""); setSearchDroite("");
      setPresseUtilisee(""); setDureeCycle(""); setCalesUtilisees("");
      setCommentaire(""); setTraitementApresCuisson(""); setCommentairesGenerales("");
      setSuccess("✅ Données enregistrées avec succès.");
    } catch (err) {
      setError("❌ " + (err.message || "Erreur d'envoi."));
    } finally {
      setSending(false);
    }
  }

  const timeStr = now.toLocaleTimeString("fr-FR");
  const dateStr = now.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });

  return (
    <div className="fab-page">
      <div className="fab-card">

        <div className="fab-header">
          <span>Fabrication</span>
          <span className="fab-header-sub">Pressage · Cuisson · Refroidissement</span>
        </div>

        <div className="fab-nav">
          <button type="button" className="fab-btn-back" onClick={() => navigate("/menu")}>← Retour menu</button>
          <div className="fab-clock">
            <div className="fab-clock-time">{timeStr}</div>
            <div className="fab-clock-date">{dateStr}</div>
          </div>
        </div>

        <div className="fab-operator">
          <span className="fab-operator-dot" />
          {operator ? operator.toUpperCase() : "OPÉRATEUR NON CONNECTÉ"}
        </div>

        <form onSubmit={handleSubmit} className="fab-form">

          {/* PARCS PRESSE */}
          <div className="fab-dual-grid">
            <ParcInput label="Presse Position Gauche" value={parcPresseGauche} onChange={setParcPresseGauche} onScan={() => openScan("presseGauche")} />
            <ParcInput label="Presse Position Droite" value={parcPresseDroite} onChange={setParcPresseDroite} onScan={() => openScan("presseDroite")} />
          </div>

          {/* PARCS EMPILEMENT */}
          <div className="fab-dual-grid">
            <EmpilementInput
              label="Empilement Position Gauche"
              value={parcEmpilementGauche} searchValue={searchGauche}
              onSearchChange={setSearchGauche}
              onSelect={(p) => { setParcEmpilementGauche(p); setSearchGauche(p); }}
              onClear={() => { setParcEmpilementGauche(""); setSearchGauche(""); }}
              onScan={() => openScan("empilementGauche")}
              showList={showListGauche} setShowList={setShowListGauche}
              filteredList={filteredGauche} boxRef={boxRefGauche}
            />
            <EmpilementInput
              label="Empilement Position Droite"
              value={parcEmpilementDroite} searchValue={searchDroite}
              onSearchChange={setSearchDroite}
              onSelect={(p) => { setParcEmpilementDroite(p); setSearchDroite(p); }}
              onClear={() => { setParcEmpilementDroite(""); setSearchDroite(""); }}
              onScan={() => openScan("empilementDroite")}
              showList={showListDroite} setShowList={setShowListDroite}
              filteredList={filteredDroite} boxRef={boxRefDroite}
            />
          </div>

          {/* BLOC PRESSAGE / CUISSON */}
          <div className="fab-section fab-section-yellow">
            <div className="fab-section-label">🔥 Cycle pressage / Cuisson</div>
            <div className="fab-grid-auto">
              <div className="fab-col">
                <div className="fab-col-label">Recette pressage</div>
                <input value={presseUtilisee} onChange={(e) => setPresseUtilisee(e.target.value)} className="fab-grid-input" />
              </div>
              <div className="fab-col">
                <div className="fab-col-label">Durée du cycle</div>
                <input value={dureeCycle} onChange={(e) => setDureeCycle(e.target.value)} className="fab-grid-input" />
              </div>
              <div className="fab-col">
                <div className="fab-col-label">Cales utilisées</div>
                <input value={calesUtilisees} onChange={(e) => setCalesUtilisees(e.target.value)} className="fab-grid-input" />
              </div>
              <div className="fab-col">
                <div className="fab-col-label">Traitement après cuisson</div>
                <input value={traitementApresCuisson} onChange={(e) => setTraitementApresCuisson(e.target.value)} className="fab-grid-input" />
              </div>
            </div>
            <div>
              <div className="fab-wide-label">💬 Commentaire</div>
              <input value={commentaire} onChange={(e) => setCommentaire(e.target.value)} className="fab-wide-input" placeholder="Commentaire pressage…" />
            </div>
          </div>

          {/* COMMENTAIRES GÉNÉRAUX */}
          <div className="fab-section">
            <div className="fab-section-label">📝 Commentaires généraux</div>
            <input value={commentairesGenerales} onChange={(e) => setCommentairesGenerales(e.target.value)}
              className="fab-wide-input" placeholder="Observations générales…" />
          </div>

          {error   && <div className="fab-msg fab-msg-error">{error}</div>}
          {success && <div className="fab-msg fab-msg-success">{success}</div>}

          <div className="fab-submit-row">
            <button type="submit" disabled={sending} className="fab-btn-submit">
              {sending ? "⏳ Envoi en cours…" : "✔ OK confirmation et envoi"}
            </button>
            <button type="button" className="fab-btn-time">
              Timestamps
              <div className="fab-btn-time-sub">{timeStr}</div>
            </button>
          </div>

        </form>
      </div>

      {showScanner && (
        <div className="fab-modal-backdrop" onClick={() => setShowScanner(false)}>
          <div className="fab-modal-scanner" onClick={(e) => e.stopPropagation()}>
            <div className="fab-modal-title">📷 Scanner Parc Number</div>
            <div style={{ borderRadius: 10, overflow: "hidden" }}>
              <Scanner onScan={handleScan} onError={(e) => console.error(e)} constraints={{ facingMode: "environment" }} />
            </div>
            <button type="button" className="fab-btn-close-scanner" onClick={() => setShowScanner(false)}>✕ Fermer</button>
          </div>
        </div>
      )}

      <Style />
    </div>
  );
}

function Style() {
  return (
    <style>{`
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

      .fab-page {
        min-height: 100vh;
        width: 100%;
        background: radial-gradient(ellipse at top, #01283a 0%, #010F14 70%);
        display: flex;
        justify-content: center;
        align-items: flex-start;
        padding: clamp(10px, 3vw, 32px);
        font-family: 'Inter', 'Segoe UI', sans-serif;
      }

      .fab-card {
        width: 100%;
        max-width: min(960px, 100%);
        background: #011e2b;
        border: 1px solid #1a4a5a;
        border-radius: clamp(12px, 2vw, 18px);
        overflow: hidden;
        box-shadow: 0 8px 40px rgba(0,0,0,0.6);
      }

      /* HEADER */
      .fab-header {
        background: linear-gradient(135deg, #E6FF3E, #c8e800);
        color: #000;
        padding: clamp(12px, 2vw, 20px) clamp(16px, 4vw, 40px);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 3px;
      }
      .fab-header span   { font-size: clamp(14px, 2.5vw, 22px); font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em; }
      .fab-header-sub    { font-size: clamp(10px, 1.5vw, 13px); font-weight: 500; opacity: 0.6; }

      /* NAV */
      .fab-nav {
        background: #010f14;
        padding: clamp(8px, 1.5vw, 14px) clamp(12px, 3vw, 32px);
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid #1a4a5a;
        gap: 8px;
      }
      .fab-btn-back {
        background: transparent;
        color: #7ab8cc;
        border: 1px solid #1a4a5a;
        border-radius: 6px;
        padding: 5px 10px;
        font-size: clamp(10px, 1.5vw, 13px);
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
        white-space: nowrap;
      }
      .fab-btn-back:hover { background: #1a4a5a; color: #fff; }
      .fab-clock          { text-align: right; }
      .fab-clock-time     { color: #E6FF3E; font-size: clamp(13px, 2vw, 20px); font-weight: 700; font-variant-numeric: tabular-nums; }
      .fab-clock-date     { color: #5a8a99; font-size: clamp(9px, 1.2vw, 12px); }

      /* OPÉRATEUR */
      .fab-operator {
        display: flex;
        align-items: center;
        gap: 10px;
        background: linear-gradient(90deg, #012836, #013d50);
        border: 1px solid #1a5a6a;
        border-radius: 10px;
        margin: clamp(10px, 2vw, 18px) clamp(12px, 3vw, 36px) 0;
        padding: clamp(10px, 1.5vw, 16px) clamp(12px, 2vw, 22px);
        color: #fff;
        font-size: clamp(12px, 1.8vw, 18px);
        font-weight: 700;
        letter-spacing: 0.06em;
      }
      .fab-operator-dot {
        width: clamp(8px, 1.2vw, 12px);
        height: clamp(8px, 1.2vw, 12px);
        background: #00B050;
        border-radius: 50%;
        box-shadow: 0 0 6px #00B050;
        flex-shrink: 0;
      }

      /* FORM */
      .fab-form {
        padding: clamp(12px, 2.5vw, 28px) clamp(12px, 3vw, 36px) clamp(20px, 3vw, 36px);
        display: flex;
        flex-direction: column;
        gap: clamp(12px, 2vw, 22px);
      }

      /* GRILLE 2 COLONNES — auto sur petits écrans */
      .fab-dual-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
        gap: clamp(10px, 2vw, 16px);
      }

      /* SECTIONS */
      .fab-section {
        background: #012030;
        border: 1px solid #1a4a5a;
        border-radius: clamp(10px, 1.5vw, 14px);
        padding: clamp(12px, 2vw, 20px);
        display: flex;
        flex-direction: column;
        gap: clamp(8px, 1.5vw, 14px);
      }
      .fab-section-yellow { background: #1a1400; border-color: #FFD966; }
      .fab-section-label  { font-size: clamp(10px, 1.4vw, 14px); font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #7ab8cc; }
      .fab-section-label-green { color: #7aff9a; }

      /* PARC ROW */
      .fab-parc-row   { display: flex; gap: 8px; align-items: center; }
      .fab-parc-input {
        flex: 1;
        min-width: 0;
        background: #003818;
        color: #7aff9a;
        border: 1px solid #005020;
        border-radius: 8px;
        padding: clamp(8px, 1.5vw, 14px) clamp(10px, 2vw, 18px);
        font-size: clamp(13px, 1.8vw, 18px);
        font-weight: 600;
        outline: none;
        transition: border 0.2s;
      }
      .fab-parc-input:focus { border-color: #00B050; box-shadow: 0 0 0 2px rgba(0,176,80,0.15); }
      .fab-scan-btn {
        width: clamp(40px, 6vw, 58px);
        height: clamp(40px, 6vw, 58px);
        flex-shrink: 0;
        background: #012030;
        border: 1px solid #1a4a5a;
        border-radius: 8px;
        font-size: clamp(16px, 2.5vw, 26px);
        cursor: pointer;
        transition: background 0.2s;
      }
      .fab-scan-btn:hover { background: #1a4a5a; }

      /* SELECTED BADGE */
      .fab-selected-badge {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
        margin-top: 6px;
        background: #002810;
        border: 1px solid #00B050;
        border-radius: 8px;
        padding: 6px 12px;
        font-size: clamp(11px, 1.4vw, 14px);
        color: #7aff9a;
      }
      .fab-selected-badge strong { flex: 1; min-width: 0; word-break: break-all; }
      .fab-clear-btn { background: transparent; border: none; color: #ff8080; cursor: pointer; font-size: 14px; font-weight: 700; flex-shrink: 0; }

      /* DROPDOWN */
      .fab-dropdown {
        position: absolute; left: 0; right: 0; margin-top: 4px;
        max-height: 180px; overflow-y: auto;
        background: #002818; border: 1px solid #005020; border-radius: 8px; z-index: 50;
        box-shadow: 0 8px 24px rgba(0,0,0,0.5);
      }
      .fab-dropdown-item {
        padding: clamp(7px, 1.2vw, 10px) clamp(10px, 2vw, 16px);
        color: #aaffcc;
        font-size: clamp(12px, 1.5vw, 15px);
        cursor: pointer;
        border-bottom: 1px solid #003818;
        transition: background 0.15s;
      }
      .fab-dropdown-item:hover { background: #003d20; }

      /* GRILLE AUTO — 1 col sur mobile, 4 sur large */
      .fab-grid-auto {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
        gap: clamp(8px, 1.5vw, 14px);
      }
      .fab-col { display: flex; flex-direction: column; gap: 5px; }
      .fab-col-label {
        font-size: clamp(9px, 1.2vw, 13px);
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: #FFD966;
        text-align: center;
      }
      .fab-grid-input {
        width: 100%;
        background: #010F14;
        color: #fff;
        border: 1px solid #1a4a5a;
        border-radius: 8px;
        padding: clamp(7px, 1.2vw, 12px) 6px;
        font-size: clamp(12px, 1.5vw, 15px);
        text-align: center;
        font-weight: 600;
        outline: none;
        transition: border 0.2s;
      }
      .fab-grid-input:focus { border-color: #E6FF3E; box-shadow: 0 0 0 2px rgba(230,255,62,0.1); }

      /* WIDE INPUT */
      .fab-wide-label {
        font-size: clamp(10px, 1.3vw, 13px);
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: #7ab8cc;
        margin-bottom: 5px;
      }
      .fab-wide-input {
        width: 100%;
        background: #010F14;
        color: #fff;
        border: 1px solid #1a4a5a;
        border-radius: 8px;
        padding: clamp(9px, 1.5vw, 14px) clamp(10px, 2vw, 18px);
        font-size: clamp(12px, 1.5vw, 15px);
        font-weight: 500;
        outline: none;
        transition: border 0.2s;
      }
      .fab-wide-input:focus { border-color: #E6FF3E; box-shadow: 0 0 0 2px rgba(230,255,62,0.1); }

      /* MESSAGES */
      .fab-msg         { border-radius: 8px; padding: clamp(9px, 1.5vw, 14px); font-size: clamp(12px, 1.5vw, 15px); font-weight: 600; }
      .fab-msg-error   { background: #2a0808; border: 1px solid #C00000; color: #ff9090; }
      .fab-msg-success { background: #002810; border: 1px solid #00B050; color: #80ff90; }

      /* SUBMIT */
      .fab-submit-row {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        align-items: stretch;
      }
      .fab-btn-submit {
        flex: 1;
        min-width: 200px;
        background: linear-gradient(135deg, #00B050, #008a3c);
        color: #000;
        border: none;
        border-radius: 10px;
        padding: clamp(12px, 2vw, 18px);
        font-size: clamp(13px, 1.8vw, 18px);
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        cursor: pointer;
        transition: opacity 0.2s, transform 0.1s;
        box-shadow: 0 4px 16px rgba(0,176,80,0.3);
      }
      .fab-btn-submit:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
      .fab-btn-submit:disabled { opacity: 0.5; cursor: not-allowed; }
      .fab-btn-time {
        min-width: 110px;
        background: #C00000;
        color: #fff;
        border: 2px solid #7F0000;
        border-radius: 8px;
        padding: clamp(8px, 1.2vw, 12px) clamp(10px, 1.5vw, 16px);
        font-weight: 700;
        font-size: clamp(11px, 1.3vw, 14px);
        cursor: default;
        text-align: center;
      }
      .fab-btn-time-sub { font-size: clamp(10px, 1.2vw, 13px); margin-top: 2px; font-variant-numeric: tabular-nums; }

      /* SCANNER MODAL */
      .fab-modal-backdrop {
        position: fixed; inset: 0; background: rgba(0,0,0,0.8);
        display: flex; justify-content: center; align-items: center;
        z-index: 999; backdrop-filter: blur(4px);
        padding: clamp(12px, 3vw, 24px);
      }
      .fab-modal-scanner {
        width: 100%; max-width: 420px;
        background: #011e2b; border: 1px solid #00B050; border-radius: 14px;
        padding: clamp(14px, 2.5vw, 24px);
        display: flex; flex-direction: column; gap: 12px;
        box-shadow: 0 0 40px rgba(0,176,80,0.2);
      }
      .fab-modal-title { color: #fff; font-size: clamp(13px, 1.8vw, 16px); font-weight: 700; text-align: center; }
      .fab-btn-close-scanner {
        background: #C00000; color: #fff; border: none; border-radius: 8px;
        padding: clamp(9px, 1.5vw, 12px); font-weight: 700;
        font-size: clamp(12px, 1.5vw, 14px); cursor: pointer; transition: opacity 0.2s;
      }
      .fab-btn-close-scanner:hover { opacity: 0.85; }
    `}</style>
  );
}